import { FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { ComposableDeviceValues } from '../AddComposableDevice';
import { ErrorText } from '../../../../../theme/components/Forms';
import * as yup from 'yup';
import {
  StyledAutoComplete,
  StyledInputLabel,
  StyledStack,
  StyledTextField,
} from './StyledElements';
import {
  Box,
  FormControl,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { useAppSelector } from '../../../../../redux/store.model';
import { getMachineTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { getAllAvailableDevicesToAddByGroupIdSelector } from '../../../../../redux/commander/selectors';
import _ from 'lodash';
import { ST_COMPOSABLE_CONSUMPTION_DATA_KEY } from '../../../../../redux/composableDevice/ComposableDevice.model';

export const DecksForm = ({
  formik,
}: {
  formik: FormikProps<ComposableDeviceValues>;
}) => {
  const polyglot = getPolyglot();
  const theme = useTheme();

  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: formik.values.composableDevice?.machineTypeId,
    })
  );

  const commandersQuantity =
    Number(formik.values.composableDevice?.labels?.commandersQuantity) ||
    Number(machineType?.labels.commandersQuantity) ||
    0;

  const labels = formik.values.composableDevice?.labels || {};
  const labelsObject = JSON.parse(
    labels[ST_COMPOSABLE_CONSUMPTION_DATA_KEY] || '{}'
  );
  const [deviceValues, setDeviceValues] = useState<string[]>(
    formik.values?.devices?.length
      ? formik.values?.devices
      : Array.from({ length: +commandersQuantity }).map(() => '')
  );
  const [powerValues, setPowerValues] = useState<number[]>(
    labelsObject
      ? Object.values(labelsObject)
          .map(Number)
          .map((v) => (isNaN(v) ? 0 : v))
      : Array.from({ length: +commandersQuantity }).map(() => 0)
  );

  const devices = useAppSelector((state) =>
    getAllAvailableDevicesToAddByGroupIdSelector(state, {
      groupId: undefined,
    })
  );

  useEffect(() => {
    if (deviceValues.length > 0) {
      const value = deviceValues.reduce((acc, curr, index) => {
        acc[curr] = powerValues[index] || 0;
        return acc;
      }, {} as any);
      formik.setFieldValue('devices', deviceValues);
      formik.setFieldValue(
        `composableDevice.labels.ST_COMPOSABLE_CONSUMPTION_DATA_KEY`,
        JSON.stringify(value)
      );
      (async () => {
        await formik.validateField('devices');
        formik.setFieldTouched('devices', true, true);
        formik.validateForm();
      })();
    }
  }, [deviceValues, powerValues]);

  return (
    <Stack display={'flex'} flexDirection={'column'}>
      {Array.from({ length: +commandersQuantity }).map((_, index) => (
        <StyledStack
          key={index}
          sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}
        >
          <FormControl>
            <StyledInputLabel htmlFor={`devices-select-deck-${index}`}>
              {polyglot.t('fours_fringand.oven.shelf', {
                number: index + 1,
              })}
            </StyledInputLabel>
            <StyledAutoComplete
              id={`devices-select-deck-${index}`}
              options={devices}
              autoHighlight
              getOptionLabel={(option: any) => option.name || ''}
              value={
                devices.find((d) => deviceValues[index] === d.uuid) || null
              }
              onChange={(_, value: any) => {
                setDeviceValues((prevValues) => {
                  const newValues = [...prevValues];
                  newValues[index] = value?.uuid || '';
                  return newValues;
                });
              }}
              renderOption={(props: any, option: any) => {
                const { key, ...optionProps } = props;
                return (
                  <Box key={key} component="li" {...optionProps}>
                    {option.name || ''}
                  </Box>
                );
              }}
              renderInput={(params) => <StyledTextField {...params} />}
            />
          </FormControl>
          <FormControl>
            <StyledInputLabel htmlFor={`power-value-input-${index}`}>
              {polyglot.t('composable_device.power_value')}
            </StyledInputLabel>
            <StyledTextField
              id={`power-value-input-${index}`}
              name={`powerValue-${index}`}
              type="number"
              inputProps={{ min: 0, placeholder: '0' }}
              value={powerValues[index] || ''}
              onChange={(e) => {
                setPowerValues((prevValues) => {
                  const newValues = [...prevValues];
                  newValues[index] = Number(e.target.value);
                  return newValues;
                });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{
                      maxHeight: '100%',
                      height: '100%',
                      margin: 0,
                      backgroundColor: theme.palette.common.white,
                    }}
                  >
                    <Typography
                      sx={{
                        display: 'flex',
                        height: '100%',
                        alignItems: 'center',
                        backgroundColor: theme.palette.common.white,
                        margin: '0 0.5rem',
                      }}
                      variant="body2"
                    >
                      kW
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </StyledStack>
      ))}
      {formik.errors.devices && formik.touched.devices && (
        <ErrorText fontSize={'0.9rem'} sx={{ margin: '-1rem 0 1rem 0' }}>
          {formik.errors.devices}
        </ErrorText>
      )}

      <StyledStack>
        <FormControl>
          <StyledInputLabel htmlFor={`kw-value`}>
            {polyglot.t(
              'composable_device.dashboard_consumption_setting_kwh_price'
            )}
          </StyledInputLabel>
          <StyledTextField
            id={`kw-value`}
            name={`kw-value`}
            type="number"
            inputProps={{ min: 0, step: 0.1 }}
            onChange={(e) => {
              formik.setFieldValue(
                'composableDevice.labels.ST_KILO_WATT_EUR_CONSUMPTION',
                e.target.value
              );
            }}
            value={
              formik.values.composableDevice?.labels
                ?.ST_KILO_WATT_EUR_CONSUMPTION || ''
            }
            InputProps={{
              placeholder: '1,24',
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    maxHeight: '100%',
                    height: '100%',
                    margin: 0,
                    backgroundColor: theme.palette.common.white,
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      height: '100%',
                      alignItems: 'center',
                      backgroundColor: theme.palette.common.white,
                      margin: '0 0.5rem',
                    }}
                    variant="body2"
                  >
                    €
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </StyledStack>
    </Stack>
  );
};
